import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';

import { cn } from '@/components/ui/cn';
import { Select } from '@/components/ui/Select';
import type { CatalogItem, NormClause } from '@/lib/quality-catalogs';

import type { ExtraFilter, StatusSegment, UiFilters } from '../use-url-filters';
import { ActiveFilterChips } from './ActiveFilterChips';
import { MoreFilters } from './MoreFilters';

/** Catálogos de "+ Filtro" (`undefined` mientras cargan); el FilterBar recibe null sin
 *  permiso de catálogos. */
export type CatalogKey = 'norms' | 'types' | 'areas';

export interface FilterCatalogs {
  norms: NormClause[] | undefined;
  types: CatalogItem[] | undefined;
  areas: CatalogItem[] | undefined;
  /** Catálogos que fallaron al cargar. */
  failed: Readonly<Record<CatalogKey, boolean>>;
  retry: (key: CatalogKey) => void;
}

const SEGMENTS: { value: StatusSegment; label: string }[] = [
  { value: 'todos', label: 'Todos' },
  { value: 'vigente', label: 'Vigentes' },
  { value: 'en_revision', label: 'En revisión' },
  { value: 'por_firmar', label: 'Por firmar' },
  { value: 'mis_firmas', label: 'Me toca firmar' },
  { value: 'en_capacitacion', label: 'En capacitación' },
  { value: 'vencidos', label: 'Vencidos' },
  { value: 'obsoleto', label: 'Obsoletos' },
  { value: 'anulado', label: 'Anulados' },
];

interface Props {
  filters: UiFilters;
  catalogs: FilterCatalogs | null;
  onSegment: (s: StatusSegment) => void;
  onQuery: (q: string) => void;
  onSetFilters: (next: Partial<Record<ExtraFilter, string | null>>) => void;
  onClearFilter: (f: ExtraFilter) => void;
  className?: string;
  style?: CSSProperties;
}

export function FilterBar({
  filters,
  catalogs,
  onSegment,
  onQuery,
  onSetFilters,
  onClearFilter,
  className,
  style,
}: Props): ReactElement {
  const [text, setText] = useState(filters.q);
  const [open, setOpen] = useState(false);

  // Sincroniza el input si la URL cambia por fuera (deep-link, back/forward).
  useEffect(() => {
    setText(filters.q);
  }, [filters.q]);

  // Debounce: refleja la búsqueda en la URL 300 ms después de dejar de teclear.
  useEffect(() => {
    if (text === filters.q) {
      return;
    }
    const id = setTimeout(() => onQuery(text), 300);
    return () => clearTimeout(id);
  }, [text, filters.q, onQuery]);

  return (
    <div className={cn('filters', className)} style={style}>
      <label className="search">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Buscar por código o título…"
          aria-label="Buscar en la lista maestra"
        />
      </label>

      {/* Segmentos si caben en la barra; si no, un selector con los mismos estados (mismo
          valor). Lo decide el ancho de la barra (container query), no el de la pantalla. */}
      <div className="seg seg-wide" role="group" aria-label="Filtrar por estado">
        {SEGMENTS.map((s) => (
          <button
            key={s.value}
            type="button"
            className={cn(filters.segment === s.value && 'on')}
            aria-pressed={filters.segment === s.value}
            onClick={() => onSegment(s.value)}
          >
            {s.label}
          </button>
        ))}
      </div>
      <label className="fld seg-narrow">
        <span className="fld-lab">Estado</span>
        <Select
          value={filters.segment}
          options={SEGMENTS}
          onChange={(v) => onSegment(v as StatusSegment)}
        />
      </label>

      <ActiveFilterChips filters={filters} catalogs={catalogs} onClear={onClearFilter} />

      {catalogs !== null && (
        <button
          type="button"
          className="chipf add"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          + Filtro
        </button>
      )}

      {open && catalogs !== null && (
        <MoreFilters filters={filters} catalogs={catalogs} onSetFilters={onSetFilters} />
      )}
    </div>
  );
}
